/* <predictive-search>: combobox enhancement over the header's details/summary
   search. Uses Shopify's predictive Section Rendering API; every failure path
   degrades to the plain GET /search form. */
(() => {
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-predictive-panel]');
      if (!this.input || !this.panel) return;

      this.abort = null;
      this.timer = null;
      this.activeIndex = -1;

      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-expanded', 'false');
      /* Point at the panel, which is in the DOM from first paint. The old value
         was 'PredictiveResults' — the id of the results div inside the fetched
         section, which does not exist until someone has typed two characters,
         so every page shipped a combobox whose aria-controls referenced nothing. */
      if (!this.panel.id) this.panel.id = 'PredictiveResults-panel';
      this.input.setAttribute('aria-controls', this.panel.id);
      this.input.setAttribute('aria-autocomplete', 'list');

      this.input.addEventListener('input', () => {
        window.clearTimeout(this.timer);
        const terms = this.input.value.trim();
        if (terms.length < 2) {
          this.close();
          return;
        }
        this.timer = window.setTimeout(() => this.fetchSuggestions(terms), 250);
      });

      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.close();
      });

      this.setupHover();
    }

    /* Hover-to-open on pointer-fine devices, mirroring the cart hover preview:
       reveal the search panel on hover, dismiss on mouse-leave, scroll, or an
       outside click. Touch and keyboard keep the native <details> toggle. */
    setupHover() {
      this.details = this.querySelector('details');
      if (!this.details) return;
      this.summary = this.querySelector('summary');
      this.hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.hideTimer = null;

      this.addEventListener('mouseenter', () => this.hoverOpen());
      this.addEventListener('mouseleave', () => this.scheduleHoverClose());

      // A real mouse click lets hover own open/close (so the native toggle
      // doesn't fight the hover state). Keyboard activation (detail === 0)
      // still toggles the <details> natively.
      this.summary?.addEventListener('click', (event) => {
        if (this.hoverable.matches && event.detail > 0) event.preventDefault();
      });

      window.addEventListener(
        'scroll',
        () => {
          if (this.details.open) this.hoverClose();
        },
        { passive: true }
      );
      document.addEventListener('pointerdown', (event) => {
        if (this.details.open && !this.contains(event.target)) this.hoverClose();
      });
    }

    hoverOpen() {
      if (!this.hoverable?.matches) return;
      window.clearTimeout(this.hideTimer);
      this.details.open = true;
    }

    scheduleHoverClose() {
      if (!this.hoverable?.matches) return;
      window.clearTimeout(this.hideTimer);
      this.hideTimer = window.setTimeout(() => this.hoverClose(), 250);
    }

    hoverClose() {
      window.clearTimeout(this.hideTimer);
      if (this.details) this.details.open = false;
      this.close();
    }

    async fetchSuggestions(terms) {
      this.abort?.abort();
      this.abort = new AbortController();
      try {
        const url = `${window.Shopify?.routes?.root || '/'}search/suggest?q=${encodeURIComponent(terms)}&section_id=predictive-search&resources[limit]=4&resources[limit_scope]=each`;
        const response = await fetch(url, { signal: this.abort.signal });
        if (!response.ok) throw new Error(`suggest ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const region = doc.querySelector('[data-predictive-region]');
        if (!region) throw new Error('no region');
        this.panel.replaceChildren(region);
        this.activeIndex = -1;
        this.input.setAttribute('aria-expanded', 'true');
        this.panel.removeAttribute('hidden');
        const count = region.querySelector('[data-predictive-count]')?.dataset.predictiveCount || '0';
        const live = this.querySelector('[data-predictive-status]');
        if (live) live.textContent = `${count} suggestions`;
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }

    options() {
      return [...this.panel.querySelectorAll('[role="option"]')];
    }

    onKeydown(event) {
      const options = this.options();
      if (event.key === 'Escape') {
        if (this.input.getAttribute('aria-expanded') === 'true') {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        }
        return;
      }
      if (!options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + delta + options.length) % options.length;
        options.forEach((option, index) => {
          option.classList.toggle('is-active', index === this.activeIndex);
          option.setAttribute('aria-selected', index === this.activeIndex ? 'true' : 'false');
        });
        this.input.setAttribute('aria-activedescendant', options[this.activeIndex].id);
        options[this.activeIndex].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        const link = options[this.activeIndex].querySelector('a');
        if (link) {
          event.preventDefault();
          window.location.assign(link.href);
        }
      }
    }

    close() {
      this.abort?.abort();
      this.panel.setAttribute('hidden', '');
      this.panel.replaceChildren();
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
    }
  }

  if (!window.customElements.get('predictive-search')) {
    window.customElements.define('predictive-search', PredictiveSearch);
  }
})();
